import { ApiError } from "../api/client";
import type { AuthAccount, AuthEvent } from "../api/types";
import { k, locale, t } from "../i18n/i18n";

/** Textes des comptes nominatifs (docs/17 §8.3) : refus des écrans de connexion, règles des mots de passe, libellés
 *  du journal et lignes de « Depuis votre dernière connexion ». Le serveur ne renvoie jamais la saisie (§7.4) ; le
 *  client choisit le texte exact d'après le code du refus. */

export type AuthContext = "login" | "redeem" | "recover" | "bootstrap" | "unlock" | "password";

/** Bornes des mots de passe (§4.2). */
export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 128;
/** Identifiant : lettres sans accent, chiffres, point, tiret (et souligné), 3 à 40 caractères (§2.2). */
export const LOGIN_PATTERN = /^[a-z0-9._-]{3,40}$/;

/** Règle de saisie d'un nouveau mot de passe, contrôlée avant l'envoi : longueur, puis confirmation. */
export function passwordProblem(password: string, confirm: string): string | null {
  if (password.normalize("NFC").length < PASSWORD_MIN || password.length > PASSWORD_MAX) return t("12 caractères au moins.");
  if (password !== confirm) return t("Les deux saisies diffèrent.");
  return null;
}

/** Délai d'attente d'un refus 429, en secondes (corps `retry_after`, sinon en-tête `Retry-After`). */
export function retrySeconds(error: ApiError): number {
  const given = error.detail?.retry_after;
  if (typeof given === "number" && given > 0) return Math.ceil(given);
  return Math.max(1, Math.ceil((error.retryAfterMs ?? 30_000) / 1000));
}

const minutes = (seconds: number) => Math.max(1, Math.ceil(seconds / 60));

/** Famille du délai rendue par le serveur (`kind`, ou `scope`, `key`), s'il la donne : `pair`, `login`, `seat`… */
function delayFamily(error: ApiError): string | null {
  const raw = error.detail?.kind ?? error.detail?.scope ?? error.detail?.key;
  return typeof raw === "string" ? raw.split(":")[0]! : null;
}

/** Trop d'essais (429) : texte du délai selon l'écran. Sans famille rendue par le serveur, un délai de plus de 30 s
 *  est celui du couple identifiant et poste (1 à 15 min), sinon celui de l'identifiant (30 s). */
function tooMany(error: ApiError, context: AuthContext): string {
  const seconds = retrySeconds(error);
  const family = delayFamily(error);
  if (context === "redeem") {
    if (family === "code-login") return t("Trop d'essais pour cet identifiant : réessayez dans {n} s.", { n: seconds });
    return t("Trop d'essais de code pour cet identifiant sur ce poste : réessayez dans {n} min.", { n: minutes(seconds) });
  }
  if (context === "recover" || context === "bootstrap")
    return seconds >= 60 ? t("Trop d'essais : réessayez dans {n} min.", { n: minutes(seconds) }) : t("Trop d'essais : réessayez dans {n} s.", { n: seconds });
  if (family === "seat")
    return t("Trop d'essais depuis ce poste pour des comptes qui ne s'y sont pas connectés récemment : réessayez dans {n} s.", { n: seconds });
  if (family === "pair" || (family === null && seconds > 30))
    return t("Trop d'essais pour cet identifiant sur ce poste : réessayez dans {n} min, ou demandez un code de réinitialisation à l'administrateur de votre unité.", {
      n: minutes(seconds),
    });
  return t("Trop d'essais pour cet identifiant : réessayez dans {n} s.", { n: seconds });
}

/** Règle de mot de passe refusée par le serveur (422 `PASSWORD_POLICY`). */
export function policyText(rule: unknown): string {
  if (rule === "common") return t("Ce mot de passe est trop courant : choisissez-en un autre.");
  if (rule === "personal") return t("Le mot de passe ne doit contenir ni votre identifiant ni votre nom.");
  return t("12 caractères au moins.");
}

/** Texte d'un refus d'un écran des comptes. */
export function authErrorText(error: unknown, context: AuthContext): string {
  if (!(error instanceof ApiError)) return t("Action impossible pour l'instant. Réessayez.");
  if (error.status === 429) return tooMany(error, context);
  switch (error.code) {
    case "AUTH_FAILED":
      return context === "unlock" || context === "password" ? t("Mot de passe incorrect.") : t("Identifiant ou mot de passe incorrect.");
    case "CODE_INVALID":
      if (context === "bootstrap") return t("Code d'amorçage incorrect ou expiré : demandez-en un nouveau à BELLICOM.");
      if (context === "recover") return t("Identifiant ou code de secours incorrect.");
      return t("Identifiant ou code incorrect, ou code expiré : demandez-en un nouveau à l'administrateur de votre unité.");
    case "ACCOUNT_LOCKED":
      return t("Ce compte est verrouillé : adressez-vous à l'administrateur de votre unité.");
    case "ACCOUNT_DISABLED":
      return t("Ce compte est désactivé : adressez-vous à l'administrateur de votre unité.");
    case "BOOTSTRAP_DONE":
      return t("Un compte administrateur vient d'être créé sur un autre poste : connectez-vous.");
    case "RECOVERY_NOT_LAST_RESORT":
      if (error.detail?.reason === "not_admin") return error.message;
      return error.detail?.reason === "target"
        ? t("Ce compte s'est connecté depuis moins de 24 h : le code de secours ne sert qu'en dernier recours. Passez par un autre administrateur ou par BELLICOM. Le code de secours n'a pas été consommé.")
        : t("Un autre administrateur de l'unité s'est connecté depuis moins de 24 h : passez par lui (avec un troisième administrateur), ou par BELLICOM. Le code de secours n'a pas été consommé.");
    case "PASSWORD_POLICY":
      return policyText(error.detail?.rule);
    case "SESSION_RESUMED":
      // Deux envois simultanés sur la même session : l'autre a abouti (docs/17 §4.10, révision 4).
      return t("Cette session vient d'être reprise dans un autre onglet de ce navigateur.");
    default:
      return error.message || t("Action impossible pour l'instant. Réessayez.");
  }
}

/** Refus d'une connexion parce que le cookie du poste n'a pas encore pris son nom sécurisé (401 `AUTH_REQUIRED`,
 *  raison `seat`, §4.4) : l'état de la licence est relu (il migre le cookie), puis l'envoi est rejoué une fois. */
export function seatNotMigrated(error: unknown): boolean {
  return error instanceof ApiError && error.code === "AUTH_REQUIRED" && error.detail?.reason === "seat";
}

// --- Dates ------------------------------------------------------------------------------------------------------

export function authDate(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString(locale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
}

export function authDateTime(iso: string | null | undefined): string {
  return iso
    ? new Date(iso).toLocaleString(locale(), { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";
}

export function authTime(iso: string | number | null | undefined): string {
  return iso !== null && iso !== undefined ? new Date(iso).toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" }) : "—";
}

// --- Journal ----------------------------------------------------------------------------------------------------

/** Libellés des actions du journal de l'unité (§8.3), par code d'action du serveur. */
export const ACTION_LABELS: Record<string, string> = {
  "account.bootstrap": k("Amorçage"),
  "ops.issue-bootstrap": k("Code d'amorçage émis (exploitation)"),
  "auth.login": k("Connexion"),
  "auth.login_failed": k("Échec de connexion"),
  "auth.logout": k("Déconnexion"),
  "auth.lock": k("Session verrouillée"),
  "auth.expired": k("Session expirée"),
  "session.revoked": k("Session fermée par un administrateur"),
  "session.failures": k("Session fermée après des essais de mot de passe"),
  "account.created": k("Compte créé"),
  "account.code_issued": k("Code remis"),
  "account.code_used": k("Code utilisé"),
  "account.code_failed": k("Code refusé"),
  "account.bootstrap_failed": k("Code refusé"),
  "recovery.used": k("Code de secours utilisé"),
  "recovery.failed": k("Code refusé"),
  "recovery.regenerated": k("Code de secours régénéré"),
  "account.password": k("Mot de passe changé"),
  "account.locked": k("Compte verrouillé"),
  "account.unlocked": k("Compte déverrouillé"),
  "account.disabled": k("Compte désactivé"),
  "account.reactivated": k("Compte réactivé"),
  "account.anonymized": k("Compte anonymisé"),
  "account.updated": k("Compte modifié"),
  "admin.named": k("Administrateur nommé"),
  "admin.removed": k("Rôle modifié"),
  "throttle.cleared": k("Délai levé"),
  "seat.unblocked": k("Blocage de poste levé"),
  "request.filed": k("Demande déposée"),
  "request.approved": k("Demande approuvée"),
  "request.refused": k("Demande refusée ou annulée"),
  "request.cancelled": k("Demande refusée ou annulée"),
  "request.expired": k("Demande expirée"),
  "unit.settings": k("Réglage modifié"),
  "licence.replace_failed": k("Identifiants d'administrateur refusés (activation)"),
  "licence.released": k("Poste libéré"),
  "licence.replaced": k("Poste repris"),
  "licence.moved": k("Poste passé sur une autre licence"),
  "exercise.created": k("Dossier créé"),
  "exercise.imported": k("Dossier importé"),
  "exercise.deleted": k("Dossier supprimé"),
  "exercise.shared": k("Dossier partagé"),
  "exercise.unshared": k("Partage retiré"),
  "exercise.owner": k("Propriétaire changé"),
  "item.taken_over": k("Propriété reprise à la désactivation"),
  "item.owner": k("Propriétaire attribué"),
  "item.visibility": k("Visibilité changée"),
  "transfer.issued": k("Code de transmission émis"),
  "transfer.cancelled": k("Code de transmission annulé"),
  "exercise.received": k("Dossier reçu"),
  "exercise.exported": k("Dossier téléchargé"),
};

/** Libellé d'une action du journal ; les gestes d'exploitation sans libellé propre sont « Exploitation ». */
export function actionLabel(action: string): string {
  const label = ACTION_LABELS[action];
  if (label) return t(label);
  if (action.startsWith("ops.")) return t("Exploitation");
  return t("Événement");
}

/** Nom du poste d'un événement : rendu par le serveur, ou dans ses détails ; « — » sinon. */
export function eventSeat(event: AuthEvent): string {
  const fromDetails = event.details?.seat;
  return event.seat || (typeof fromDetails === "string" && fromDetails) || "—";
}

/** Voie d'une nomination d'administrateur, en clair. */
function viaLabel(via: unknown): string {
  if (via === "bootstrap") return t("amorçage");
  if (via === "command") return t("exploitation");
  return t("demande");
}

/** Lignes de « Depuis votre dernière connexion » (§8.3), dans l'ordre du serveur ; les événements sans texte prévu
 *  sont écartés. `account` : la personne (une nomination qui la vise dit « Votre rôle a été changé »). */
export function sinceLines(events: AuthEvent[], account: Pick<AuthAccount, "id" | "display_name"> | null): string[] {
  const lines: string[] = [];
  for (const event of events) {
    const date = authDateTime(event.at);
    const seat = eventSeat(event);
    const by = event.actor || event.by || "—";
    const mine = Boolean(account && (event.target_id ? event.target_id === account.id : event.target === account.display_name));
    switch (event.action) {
      case "account.code_issued":
        if (event.details?.kind !== "activation")
          lines.push(t("Code de réinitialisation émis pour votre compte le {date} par {administrateur}, depuis le poste « {poste} ».", { date, administrateur: by, poste: seat }));
        break;
      case "account.code_used":
        lines.push(t("Mot de passe réinitialisé le {date} depuis le poste « {poste} ».", { date, poste: seat }));
        break;
      case "recovery.used":
        lines.push(t("Code de secours de l'unité utilisé sur votre compte le {date} depuis le poste « {poste} ».", { date, poste: seat }));
        break;
      case "session.revoked":
        lines.push(t("Vos sessions ont été fermées le {date} par {administrateur}.", { date, administrateur: by }));
        break;
      case "session.failures":
        lines.push(
          t("Votre session sur le poste « {poste} » a été fermée le {date} après {n} mots de passe incorrects.", {
            poste: seat,
            date,
            n: typeof event.details?.count === "number" ? event.details.count : 10,
          }),
        );
        break;
      case "admin.named":
      case "admin.removed":
        if (mine)
          lines.push(
            t("Votre rôle a été changé le {date} par {administrateur} : {role}.", {
              date,
              administrateur: by,
              role: event.action === "admin.named" ? t("Administrateur") : t("Utilisateur"),
            }),
          );
        else if (event.action === "admin.named")
          lines.push(t("Nouvel administrateur de l'unité : {nom}, le {date} ({voie}).", { nom: event.target || "—", date, voie: viaLabel(event.details?.via) }));
        break;
      case "auth.failures":
        lines.push(
          t("{n} échecs de connexion sur votre identifiant, le dernier le {date} depuis le poste « {poste} ».", {
            n: event.count ?? 0,
            date,
            poste: seat,
          }),
        );
        break;
      case "recovery.regenerated":
        lines.push(t("Code de secours de l'unité régénéré le {date} par {administrateur}.", { date, administrateur: by }));
        break;
      case "request.approved": {
        const kind = event.details?.kind;
        lines.push(
          t("Approbation à examiner : {action} pour {nom}, approuvée le {date} par {approbateur}.", {
            action: typeof kind === "string" ? requestLabel(kind) : t("Demande approuvée"),
            nom: event.target || "—",
            date,
            approbateur: by,
          }),
        );
        break;
      }
      case "exercise.owner":
      case "item.owner":
      case "item.taken_over": {
        // Propriété d'un élément changée par un autre compte (§5.8) : reçue ou retirée.
        const values = { nom: event.target || "—", date, auteur: by };
        if (account && event.details?.to_account === account.id)
          lines.push(t("« {nom} » vous a été transféré le {date} par {auteur} : vous en êtes propriétaire.", values));
        else if (account && event.details?.from_account === account.id)
          lines.push(t("« {nom} » a été transféré le {date} par {auteur} : vous n'en êtes plus propriétaire.", values));
        break;
      }
      case "licence.replace_failed":
        lines.push(
          t("{n} échecs d'identifiants d'administrateur lors de l'activation d'un poste, le dernier le {date} depuis l'adresse {ip}.", {
            n: event.count ?? 1,
            date,
            ip: event.ip || (typeof event.details?.ip === "string" ? event.details.ip : "—"),
          }),
        );
        break;
    }
  }
  return lines;
}

/** Geste d'une demande à deux administrateurs, en clair. */
function requestLabel(kind: string): string {
  switch (kind) {
    case "reset":
      return t("Code de réinitialisation");
    case "promote":
      return t("Nommer administrateur");
    case "demote":
      return t("Retirer le rôle d'administrateur");
    case "deactivate":
      return t("Désactiver le compte");
    default:
      return t("Demande approuvée");
  }
}
